import { isDefaultedField } from '../chunk-FV4PWIZY.js';
import { recordedConfidence } from '../chunk-X34AGPO3.js';
import { globalSingleton } from '../chunk-N7SZ66QO.js';
import '../chunk-PZ5AY32C.js';
import { parseAbiParameter, toFunctionSelector, toEventSelector, parseAbi, decodeEventLog } from 'viem';

// src/abi/types.ts
function emptyNormalizedAbi() {
  return { entries: [], reads: [], writes: [], events: [], errors: [], selectorIndex: {}, topicIndex: {} };
}
function canonicalAbiParameterType(param) {
  if (param.type.startsWith("tuple")) {
    const components = param.components ?? [];
    return `(${components.map(canonicalAbiParameterType).join(",")})${param.type.slice(5)}`;
  }
  return param.type;
}
function normalizeAbiParameter(param) {
  if (!param || typeof param.type !== "string") return param;
  const supplied = param.components;
  if (param.type.startsWith("(")) {
    try {
      const parsed = parseAbiParameter(param.type);
      if (supplied !== void 0) {
        if (!Array.isArray(supplied)) return param;
        const components = supplied.map(normalizeAbiParameter);
        const named = { ...parsed, components };
        if (canonicalAbiParameterType(named) !== canonicalAbiParameterType(parsed)) return param;
        return { ...param, type: parsed.type, components };
      }
      return { ...param, ...parsed };
    } catch {
      return param;
    }
  }
  if (Array.isArray(supplied)) {
    return { ...param, components: supplied.map(normalizeAbiParameter) };
  }
  return param;
}

// src/abi/selectors.ts
function functionSignature(fn) {
  return `${fn.name}(${(fn.inputs ?? []).map(canonicalAbiParameterType).join(",")})`;
}
function eventSignature(ev) {
  return `${ev.name}(${(ev.inputs ?? []).map(canonicalAbiParameterType).join(",")})`;
}
function errorSignature(err) {
  return `${err.name}(${(err.inputs ?? []).map(canonicalAbiParameterType).join(",")})`;
}
var SYNTHETIC_FUNCTION_NAME = /^unknown_(?:0x)?([0-9a-f]{8})$/i;
function syntheticSelector(name) {
  const match = SYNTHETIC_FUNCTION_NAME.exec(name);
  return match ? `0x${match[1].toLowerCase()}` : void 0;
}
function functionSelector(fn) {
  return syntheticSelector(fn.name) ?? toFunctionSelector(functionSignature(fn));
}
function eventTopic(ev) {
  const explicit = ev.topic;
  if (typeof explicit === "string" && /^0x[0-9a-f]{64}$/i.test(explicit)) return explicit.toLowerCase();
  return toEventSelector(eventSignature(ev));
}
var byName = (a, b) => (a.name ?? "").localeCompare(b.name ?? "");
function isReadFunction(fn) {
  if (fn.stateMutability === "view" || fn.stateMutability === "pure") return true;
  if (fn.stateMutability !== void 0) return false;
  return fn.constant === true;
}
function normalizeAbi(entries) {
  if (!Array.isArray(entries) || entries.length === 0) return emptyNormalizedAbi();
  entries = entries.map((item) => {
    if (!item || typeof item !== "object") return item;
    const fragment = item;
    return {
      ...item,
      ...Array.isArray(fragment.inputs) ? { inputs: fragment.inputs.map(normalizeAbiParameter) } : {},
      ...Array.isArray(fragment.outputs) ? { outputs: fragment.outputs.map(normalizeAbiParameter) } : {}
    };
  });
  const reads = [];
  const writes = [];
  const events = [];
  const errors = [];
  const selectorIndex = {};
  const topicIndex = {};
  for (const item of entries) {
    if (!item || typeof item !== "object") continue;
    if (item.type === "function") {
      const fn = item;
      if (isReadFunction(fn)) reads.push(fn);
      else writes.push(fn);
      try {
        selectorIndex[functionSelector(fn)] = fn;
      } catch {
      }
    } else if (item.type === "event") {
      const ev = item;
      events.push(ev);
      try {
        topicIndex[eventTopic(ev)] = ev;
      } catch {
      }
    } else if (item.type === "error") {
      errors.push(item);
    }
  }
  reads.sort(byName);
  writes.sort(byName);
  events.sort(byName);
  errors.sort(byName);
  return { entries, reads, writes, events, errors, selectorIndex, topicIndex };
}
var isNamed = (item) => typeof item.name === "string" && item.name.trim().length > 0;
function usableFragmentCount(abi) {
  return Object.values(abi.selectorIndex).filter(isNamed).length + Object.values(abi.topicIndex).filter(isNamed).length + abi.errors.filter(isNamed).length;
}
function itemKind(item) {
  if (item === null) return "null";
  if (Array.isArray(item)) return "a nested array";
  if (typeof item !== "object") return `a bare ${typeof item}`;
  const type = item.type;
  return typeof type === "string" && type.trim() ? `"${type.trim()}"` : 'an item with no "type"';
}
function describeAbiShortfall(abi) {
  if (usableFragmentCount(abi) > 0) return null;
  const total = abi.entries.length;
  if (total === 0) {
    return "This ABI is an empty array \u2014 there is nothing to decode with.";
  }
  const declared = abi.reads.length + abi.writes.length + abi.events.length;
  if (declared > 0) {
    return `Every one of the ${declared} function/event ${declared === 1 ? "item" : "items"} here is malformed \u2014 none of them yields both a name and a selector, so this ABI cannot decode anything. Check each item's \`name\` and the types in its \`inputs\`.`;
  }
  const kinds = [...new Set(abi.entries.slice(0, 20).map(itemKind))].sort();
  return `This array has ${total} ${total === 1 ? "item" : "items"} but no usable function, event or error (found ${kinds.join(
    ", "
  )}). Paste the contract's ABI array itself \u2014 not a build artifact or a response envelope with the array nested inside it.`;
}
function parseAbiJson(text2) {
  const trimmed = text2.trim();
  if (!trimmed) return { abi: null, error: new Error("ABI is empty.") };
  try {
    const parsed = JSON.parse(trimmed);
    const entries = Array.isArray(parsed) ? parsed : Array.isArray(parsed?.abi) ? parsed.abi : null;
    if (!entries) return { abi: null, error: new Error("ABI must be a JSON array of items.") };
    const abi = normalizeAbi(entries);
    const shortfall = describeAbiShortfall(abi);
    if (shortfall) return { abi: null, error: new Error(shortfall) };
    return { abi, error: null };
  } catch (err) {
    return { abi: null, error: err instanceof Error ? err : new Error(String(err)) };
  }
}
function parseHumanAbi(signatures) {
  const lines = signatures.map((s) => s.trim()).filter(Boolean);
  if (lines.length === 0) return { abi: null, error: new Error("No signatures provided.") };
  try {
    const entries = parseAbi(lines);
    return { abi: normalizeAbi(entries), error: null };
  } catch (err) {
    return { abi: null, error: err instanceof Error ? err : new Error(String(err)) };
  }
}
function abiMetaFromDto(dto) {
  return {
    chainId: dto.chain_id,
    address: dto.address,
    contractName: dto.contract_name ?? null,
    compilerVersion: dto.compiler_version ?? null,
    isProxy: dto.is_proxy,
    implementationAddresses: dto.implementation_addresses,
    source: dto.source ?? null,
    fetchedFrom: dto.fetched_from ?? null,
    confidence: dto.confidence,
    evidence: dto.evidence,
    explanation: dto.explanation,
    lifecycle: typeof dto.lifecycle === "string" ? dto.lifecycle : void 0,
    isHumanApproved: dto.is_human_approved,
    orgId: dto.org_id,
    tenantId: dto.tenant_id,
    updatedAt: dto.updated_at ?? null
  };
}
function normalizedAbiFromDto(dto) {
  return normalizeAbi(dto.entries ?? []);
}
function abiToJson(abi) {
  return JSON.stringify(abi.entries, null, 2);
}
var ABI_SHRINK_WARN_RATIO = 0.5;
function abiShrinkWarning(uploadedCount, existingCount) {
  if (!existingCount || existingCount <= 0) return null;
  if (uploadedCount <= 0) return null;
  if (uploadedCount >= existingCount) return null;
  if (uploadedCount / existingCount > ABI_SHRINK_WARN_RATIO) return null;
  const dropped = existingCount - uploadedCount;
  return `This replaces an ABI with ${existingCount} entries, and yours has ${uploadedCount}. The ${dropped} you have not included will stop decoding for this contract \u2014 a pinned ABI replaces the one it outranks rather than adding to it. Include them too if you only meant to correct part of it.`;
}

// src/abi/confidence.ts
function recordedAbiConfidence(meta) {
  return recordedConfidence({
    confidence: meta?.confidence,
    confidence_status: meta?.confidenceStatus,
    confidence_scored: meta?.confidenceScored,
    evidence: meta?.evidence?.length ? meta.evidence : meta?.explanation?.filter((line) => line.trim())
  });
}
function abiConfidenceIsRecorded(meta) {
  return recordedAbiConfidence(meta) !== null;
}
function describeUnscoredAbi(meta) {
  if (abiConfidenceIsRecorded(meta)) return null;
  const defaulted = meta != null && meta.confidence != null && isDefaultedField({ confidence: meta.confidence }, "confidence");
  return defaulted ? "Not scored \u2014 this ABI was stored with the default confidence and no evidence behind it, so there is no score to report." : "Not scored \u2014 no confidence is recorded for this ABI.";
}

// src/abi/merge.ts
function itemKey(item) {
  if (item.type === "function") return `function:${functionSignature(item)}`;
  if (item.type === "event") return `event:${eventSignature(item)}`;
  if (item.type === "error") return `error:${errorSignature(item)}`;
  return `${item.type}`;
}
function mergeNormalizedAbis(abis) {
  const seen = /* @__PURE__ */ new Set();
  const entries = [];
  for (const n of abis) {
    for (const item of n.entries) {
      const key = itemKey(item);
      if (seen.has(key)) continue;
      seen.add(key);
      entries.push(item);
    }
  }
  return normalizeAbi(entries);
}
function signatureSet(abi) {
  const out = /* @__PURE__ */ new Set();
  for (const fn of [...abi.reads, ...abi.writes]) out.add(`function:${functionSignature(fn)}`);
  for (const ev of abi.events) out.add(`event:${eventSignature(ev)}`);
  for (const err of abi.errors) out.add(`error:${errorSignature(err)}`);
  return out;
}
function diffAbis(a, b) {
  const sa = signatureSet(a);
  const sb = signatureSet(b);
  const onlyA = [...sa].filter((s) => !sb.has(s)).sort();
  const onlyB = [...sb].filter((s) => !sa.has(s)).sort();
  const common = [...sa].filter((s) => sb.has(s)).sort();
  return { onlyA, onlyB, common };
}

// src/abi/defaults.ts
var ERC20_SIGNATURES = [
  "function name() view returns (string)",
  "function symbol() view returns (string)",
  "function decimals() view returns (uint8)",
  "function totalSupply() view returns (uint256)",
  "function balanceOf(address owner) view returns (uint256)",
  "function allowance(address owner, address spender) view returns (uint256)",
  "function transfer(address to, uint256 amount) returns (bool)",
  "function approve(address spender, uint256 amount) returns (bool)",
  "function transferFrom(address from, address to, uint256 amount) returns (bool)",
  "event Transfer(address indexed from, address indexed to, uint256 value)",
  "event Approval(address indexed owner, address indexed spender, uint256 value)"
];
var ERC721_EXTRA_SIGNATURES = [
  "function ownerOf(uint256 tokenId) view returns (address)",
  "function tokenURI(uint256 tokenId) view returns (string)",
  "function getApproved(uint256 tokenId) view returns (address)",
  "function isApprovedForAll(address owner, address operator) view returns (bool)",
  "function setApprovalForAll(address operator, bool approved)",
  "function safeTransferFrom(address from, address to, uint256 tokenId)"
];
var DEFAULT_ERC_SIGNATURES = [...ERC20_SIGNATURES, ...ERC721_EXTRA_SIGNATURES];
var defaultAbi = globalSingleton("abi/default-erc", () => ({ parsed: null }));
function defaultErcAbi() {
  if (defaultAbi.parsed) return defaultAbi.parsed;
  const { abi } = parseHumanAbi(DEFAULT_ERC_SIGNATURES);
  defaultAbi.parsed = abi ?? emptyNormalizedAbi();
  return defaultAbi.parsed;
}
function coerce(abi) {
  return "topicIndex" in abi ? abi : normalizeAbi(abi);
}
function decodeLog(log, abi) {
  const normalized = coerce(abi);
  const topic0 = log.topics[0];
  const base = {
    address: log.address,
    topic0,
    blockNumber: log.blockNumber ?? null,
    logIndex: log.logIndex ?? null,
    transactionHash: log.transactionHash ?? null,
    decoded: false
  };
  if (!topic0) return { ...base, reason: "anonymous event (no topic0)" };
  const known = normalized.topicIndex[topic0];
  try {
    const result = decodeEventLog({
      abi: normalized.entries,
      data: log.data,
      topics: log.topics
    });
    return {
      ...base,
      eventName: result.eventName,
      args: result.args ?? void 0,
      decoded: true
    };
  } catch (error) {
    return {
      ...base,
      eventName: known?.name,
      reason: known ? errorMessage(error) : "unknown event (topic0 not in ABI)"
    };
  }
}
function decodeLogs(logs, abi) {
  const normalized = coerce(abi);
  return logs.map((log) => decodeLog(log, normalized));
}
function summarizeLogs(logs) {
  const counts = /* @__PURE__ */ new Map();
  for (const log of logs) {
    const name = log.eventName ?? "(unknown)";
    counts.set(name, (counts.get(name) ?? 0) + 1);
  }
  return [...counts.entries()].map(([name, count]) => ({ name, count })).sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
}
function errorMessage(error) {
  if (error instanceof Error) return error.message.split("\n")[0] ?? "decode failed";
  return "decode failed";
}

// src/abi/payloads.ts
var PAYLOAD_ENTRY_TYPE = "payload";
var PAYLOAD_SUBJECT_ARGUMENT = "argument";
var PAYLOAD_SUBJECT_EVENT = "event";
var PAYLOAD_SUBJECTS = ["ccip_message", "ccip_message_sent"];
function parsePayloadSubject(subject) {
  const match = typeof subject === "string" ? /^(argument|event):0x([0-9a-f]+):([0-9]+)$/i.exec(subject.trim()) : null;
  if (!match) return null;
  const family = match[1].toLowerCase();
  const hex = match[2].toLowerCase();
  if (hex.length !== (family === PAYLOAD_SUBJECT_ARGUMENT ? 8 : 64)) return null;
  return { family, key: `0x${hex}`, input: Number(match[3]) };
}
var PAYLOAD_BASES = ["verified_source", "bytecode_reading", "declared_by_owner", "execution_trace"];
var BASIS_LABELS = {
  verified_source: "read from verified source",
  bytecode_reading: "read out of the bytecode",
  declared_by_owner: "stated by the row's author",
  execution_trace: "recovered from execution traces"
};
var SUBJECT_LABELS = {
  ccip_message: "CCIP message received",
  ccip_message_sent: "CCIP message sent",
  [PAYLOAD_SUBJECT_ARGUMENT]: "argument of its own function",
  [PAYLOAD_SUBJECT_EVENT]: "argument of its own event"
};
function isPayloadEntry(entry) {
  return entry?.type === PAYLOAD_ENTRY_TYPE;
}
function payloadEntries(entries) {
  return (entries ?? []).filter(isPayloadEntry);
}
function text(value) {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}
function isCallArgumentEntry(entry) {
  return isPayloadEntry(entry) && parsePayloadSubject(entry.subject)?.family === PAYLOAD_SUBJECT_ARGUMENT;
}
function isEventDataEntry(entry) {
  return isPayloadEntry(entry) && parsePayloadSubject(entry.subject)?.family === PAYLOAD_SUBJECT_EVENT;
}
function memberIn(parts, abi) {
  for (const item of abi ?? []) {
    const entry = item;
    if (!entry || typeof entry.name !== "string" || !Array.isArray(entry.inputs)) continue;
    try {
      if (parts.family === PAYLOAD_SUBJECT_ARGUMENT && entry.type === "function" && functionSelector(entry).toLowerCase() === parts.key) return entry;
      if (parts.family === PAYLOAD_SUBJECT_EVENT && entry.type === "event" && eventTopic(entry).toLowerCase() === parts.key) return entry;
    } catch {
    }
  }
  return null;
}
function payloadBasisLabel(basis) {
  const stated = text(basis);
  if (!stated) return "basis not stated";
  return BASIS_LABELS[stated] ?? stated;
}
function payloadSubjectLabel(subject) {
  const parts = parsePayloadSubject(subject);
  if (parts) return SUBJECT_LABELS[parts.family];
  const stated = text(subject);
  return stated ? SUBJECT_LABELS[stated] ?? stated : "subject not stated";
}
function payloadEntryTarget(entry, abi) {
  if (!isPayloadEntry(entry)) return null;
  const parts = parsePayloadSubject(entry.subject);
  if (!parts) return null;
  const member = memberIn(parts, abi);
  const inputs = member?.inputs ?? [];
  const signature = member ? `${member.name}(${inputs.map(canonicalAbiParameterType).join(",")})` : parts.key;
  const input = inputs[parts.input]?.name ? `input ${parts.input} (${inputs[parts.input].name})` : `input ${parts.input}`;
  const path = text(entry.path);
  return [signature, input, ...path ? [`path ${path}`] : []].join(" \xB7 ");
}
function payloadEntryFields(entry) {
  if (!isPayloadEntry(entry)) return [];
  const layout = entry.layout ?? {};
  const packed = layout.layout === "packed";
  const list = packed ? layout.fields : layout.components;
  if (!Array.isArray(list)) return [];
  return list.map((field) => {
    const name = text(field?.name) ?? "?";
    if (!packed) {
      const type2 = typeof field?.type === "string" ? canonicalAbiParameterType(field) : "?";
      return `${type2} ${name}`;
    }
    const type = text(field?.type) ?? "?";
    const at = typeof field?.offset === "number" ? `@${field.offset}` : "";
    const size = typeof field?.size === "number" ? `+${field.size}` : "";
    const label = type === "zero" && !text(field?.name) ? "(padding)" : name;
    return `${type} ${label}${at || size ? ` ${at}${size}` : ""}`;
  });
}
function payloadEntrySignature(entry, abi) {
  if (!isPayloadEntry(entry)) return "";
  const target = payloadEntryTarget(entry, abi);
  const subject = String(entry.subject ?? "?");
  const where = target ? `${subject} ${target}` : subject;
  const packed = entry.layout?.layout === "packed" ? "packed " : "";
  return `payload ${String(entry.name ?? "")} [${where} \xB7 ${String(entry.basis ?? "?")}] ${packed}(${payloadEntryFields(entry).join(", ")})`;
}
function payloadEntryNotes(entry) {
  return isPayloadEntry(entry) ? text(entry.evidence?.notes) : null;
}
function payloadEntryWitnesses(entry) {
  const raw = isPayloadEntry(entry) ? entry.evidence?.witnesses : void 0;
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((item) => {
    if (!item || typeof item !== "object") return [];
    const row = item;
    const witness = {};
    for (const key of ["field", "by", "tx"]) {
      const value = text(row[key]);
      if (value) witness[key] = value;
    }
    return Object.keys(witness).length ? [witness] : [];
  });
}
function payloadEntryGuard(entry) {
  const when = isPayloadEntry(entry) ? entry.when : void 0;
  if (!when || typeof when !== "object" || Array.isArray(when)) return null;
  const pairs = Object.entries(when).map(([key, value]) => `${key} = ${String(value)}`);
  return pairs.length ? pairs.join(", ") : null;
}
function payloadEntryKey(entry) {
  if (!isPayloadEntry(entry)) return "";
  const parts = parsePayloadSubject(entry.subject);
  const subject = parts ? `${parts.family}:${parts.key}:${parts.input}` : entry.subject;
  return JSON.stringify([subject, entry.path ?? null, entry.name, entry.when ?? null]);
}

export { ABI_SHRINK_WARN_RATIO, DEFAULT_ERC_SIGNATURES, PAYLOAD_BASES, PAYLOAD_ENTRY_TYPE, PAYLOAD_SUBJECTS, PAYLOAD_SUBJECT_ARGUMENT, PAYLOAD_SUBJECT_EVENT, SYNTHETIC_FUNCTION_NAME, abiConfidenceIsRecorded, abiMetaFromDto, abiShrinkWarning, abiToJson, canonicalAbiParameterType, decodeLog, decodeLogs, defaultErcAbi, describeAbiShortfall, describeUnscoredAbi, diffAbis, emptyNormalizedAbi, errorSignature, eventSignature, eventTopic, functionSelector, functionSignature, isCallArgumentEntry, isEventDataEntry, isPayloadEntry, isReadFunction, itemKey, mergeNormalizedAbis, normalizeAbi, normalizeAbiParameter, normalizedAbiFromDto, parseAbiJson, parseHumanAbi, parsePayloadSubject, payloadBasisLabel, payloadEntries, payloadEntryFields, payloadEntryGuard, payloadEntryKey, payloadEntryNotes, payloadEntrySignature, payloadEntryTarget, payloadEntryWitnesses, payloadSubjectLabel, recordedAbiConfidence, signatureSet, summarizeLogs, syntheticSelector, usableFragmentCount };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map